import React, { useCallback, useMemo } from 'react';

import { useSnackbar } from 'notistack';

import { ContentCopy as ContentCopyIcon, Download as DownloadIcon } from '@mui/icons-material';
import { Box, Button, Stack, Tooltip, type ButtonProps } from '@mui/material';

interface PayloadBuilderJsonActionsProps<TState> {
  state: TState;
  fullWidth?: boolean;
  isValid: (state: TState) => boolean;
  serialize: (state: TState) => string;
  getFilename: (state: TState) => string;
  disabledTitle: string;
  copyLabel: string;
  copySuccessMessage: string;
  copyErrorMessage: string;
}

function ExportButton({
  disabled,
  disabledTitle,
  fullWidth,
  sx,
  ...buttonProps
}: ButtonProps & { disabledTitle: string; fullWidth?: boolean }) {
  return (
    <Tooltip title={disabled ? disabledTitle : ''}>
      <Box component="span" sx={{ width: fullWidth ? '100%' : undefined }}>
        <Button
          size="small"
          variant="outlined"
          color="primary"
          disabled={disabled}
          fullWidth={fullWidth}
          sx={sx}
          {...buttonProps}
        />
      </Box>
    </Tooltip>
  );
}

export default function PayloadBuilderJsonActions<TState>({
  state,
  fullWidth = false,
  isValid,
  serialize,
  getFilename,
  disabledTitle,
  copyLabel,
  copySuccessMessage,
  copyErrorMessage,
}: PayloadBuilderJsonActionsProps<TState>) {
  const { enqueueSnackbar } = useSnackbar();
  const canExport = useMemo(() => isValid(state), [isValid, state]);
  const buttonSx = fullWidth ? { flex: 1, minWidth: 0 } : undefined;

  const handleCopy = useCallback(async () => {
    if (!canExport) return;
    try {
      await navigator.clipboard.writeText(serialize(state));
      enqueueSnackbar(copySuccessMessage, { variant: 'success' });
    } catch {
      enqueueSnackbar(copyErrorMessage, { variant: 'error' });
    }
  }, [canExport, copyErrorMessage, copySuccessMessage, enqueueSnackbar, serialize, state]);

  const handleDownload = useCallback(() => {
    if (!canExport) return;
    const exportJson = serialize(state);
    const blob = new Blob([exportJson], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = getFilename(state);
    try {
      document.body.appendChild(anchor);
      anchor.click();
    } finally {
      anchor.remove();
      URL.revokeObjectURL(url);
    }
  }, [canExport, getFilename, serialize, state]);

  return (
    <Stack
      direction={fullWidth ? { xs: 'column', sm: 'row' } : 'row'}
      spacing={1}
      sx={{ width: fullWidth ? '100%' : undefined, flexWrap: fullWidth ? undefined : 'wrap' }}
      useFlexGap
    >
      <ExportButton
        disabled={!canExport}
        disabledTitle={disabledTitle}
        fullWidth={fullWidth}
        startIcon={<ContentCopyIcon fontSize="small" />}
        onClick={handleCopy}
        sx={buttonSx}
      >
        {copyLabel}
      </ExportButton>
      <ExportButton
        disabled={!canExport}
        disabledTitle={disabledTitle}
        fullWidth={fullWidth}
        startIcon={<DownloadIcon fontSize="small" />}
        onClick={handleDownload}
        sx={buttonSx}
      >
        Download .txt
      </ExportButton>
    </Stack>
  );
}
